import React, { useCallback, useEffect, useMemo, useState } from "react";
import type {
  ChatConversation,
  UploadAttachmentOptions,
} from "../../backend/api/chat";
import { conversationTitle } from "../../backend/api/chat";
import { useChatSocket } from "../../backend/chat/ChatSocketProvider";
import {
  useConversation,
  useDeleteMessage,
  useMarkRead,
  useMessages,
  useSendMessage,
  useUploadAttachment,
} from "../../backend/queries/chat";
import { Images } from "lucide-react";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import { MediaSidebar } from "./MediaSidebar";
import { MessageComposer } from "./MessageComposer";
import { MessageThread } from "./MessageThread";
import { isVideo } from "./attachmentRules";
import { typingLabel as buildTypingLabel } from "./chatFormat";
import { capturePosterFrame } from "./videoPoster";

type ConversationPaneProps = {
  conversationId: string | null;
  conversation?: ChatConversation;
  currentUserId: string | null | undefined;
  /**
   * Rendered above the thread, so the dock and the inbox can differ.
   *
   * Given a function, it receives the control that opens the shared media
   * sheet, so a surface with its own header row places the control inside it
   * rather than gaining a second bar underneath. A surface with no header, such
   * as the dock, gets the control on its own slim row.
   */
  header?: React.ReactNode | ((mediaControl: React.ReactNode) => React.ReactNode);
  /** See `MessageThread`'s prop of the same name. */
  focusMessageId?: string | null;
  onFocused?: () => void;
  className?: string;
  threadClassName?: string;
};

/**
 * Thread plus composer for one conversation.
 *
 * Shared by the client dock and the staff inbox so both surfaces behave
 * identically; only the surrounding chrome differs.
 */
export function ConversationPane({
  conversationId,
  conversation,
  currentUserId,
  header,
  focusMessageId,
  onFocused,
  className,
  threadClassName,
}: ConversationPaneProps) {
  const detail = useConversation(conversationId);
  const messages = useMessages(conversationId);
  const fetchOlder = messages.fetchNextPage;
  const loadOlder = useCallback(() => void fetchOlder(), [fetchOlder]);
  const sendMessage = useSendMessage(conversationId);
  const uploadAttachment = useUploadAttachment(conversationId);
  const deleteMessage = useDeleteMessage(conversationId);
  const markRead = useMarkRead();
  const { typingByConversation, sendTyping, status } = useChatSocket();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [mediaOpen, setMediaOpen] = useState(false);

  const participants = useMemo(
    () => detail.data?.participants ?? [],
    [detail.data],
  );

  const unread = conversation?.unread_count ?? 0;
  const markReadMutate = markRead.mutate;

  useEffect(() => {
    // Opening a thread with unread messages clears them. Guarded on the count
    // so simply having a thread open does not write on every render.
    if (conversationId && unread > 0) {
      markReadMutate(conversationId);
    }
  }, [conversationId, unread, markReadMutate]);

  const handleSend = useCallback(
    async (body: string, attachmentIds?: string[]) => {
      if (!conversationId) return;
      if (attachmentIds && attachmentIds.length > 0) {
        await sendMessage.mutateAsync({
          // Omitted rather than sent empty, so a photo with no caption stays a
          // message with no body rather than one with a blank one.
          body: body || undefined,
          attachment_ids: attachmentIds,
        });
        return;
      }
      await sendMessage.mutateAsync(body);
    },
    [conversationId, sendMessage],
  );

  const handleUpload = useCallback(
    async (file: File, options: UploadAttachmentOptions) => {
      if (!conversationId) throw new Error("No conversation is open.");
      // A video carries a still captured here, since the server does not decode
      // video. A failed capture is a missing poster, never a failed send.
      const poster = isVideo(file) ? await capturePosterFrame(file) : null;
      return uploadAttachment.mutateAsync({
        file,
        ...options,
        ...(poster ? { poster } : {}),
      });
    },
    [conversationId, uploadAttachment],
  );

  const handleTyping = useCallback(
    (typing: boolean) => {
      if (conversationId) sendTyping(conversationId, typing);
    },
    [conversationId, sendTyping],
  );

  const handleDelete = useCallback(
    (messageId: string) => {
      setDeletingId(messageId);
      deleteMessage.mutate(messageId, {
        onSettled: () => setDeletingId(null),
      });
    },
    [deleteMessage],
  );

  if (!conversationId) {
    return (
      <div
        className={cn(
          "flex flex-1 flex-col items-center justify-center gap-1 p-6 text-center",
          className,
        )}
      >
        <p className="text-sm font-medium">No conversation selected</p>
        <p className="text-sm text-muted-foreground">
          Choose a conversation to read it.
        </p>
      </div>
    );
  }

  const mediaControl = (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      onClick={() => setMediaOpen(true)}
      className="h-auto min-h-touch shrink-0 gap-1.5 text-xs md:h-7 md:min-h-0"
    >
      <Images className="h-3.5 w-3.5" aria-hidden="true" />
      Shared files
    </Button>
  );

  const typing = buildTypingLabel(
    typingByConversation[conversationId] || [],
    participants,
  );

  // A project-linked conversation stops taking new messages once the project
  // ends (SCRUM-55). The thread stays fully readable either way.
  const closed = detail.data?.closed ?? false;
  const isGroup = (conversation?.type ?? detail.data?.type) === "group";

  return (
    <div className={cn("flex min-h-0 flex-1 flex-col", className)}>
      {typeof header === "function" ? (
        header(mediaControl)
      ) : (
        <>
          {header}
          <div className="flex justify-end border-b px-3 py-1.5">{mediaControl}</div>
        </>
      )}
      <MediaSidebar
        conversationId={conversationId}
        open={mediaOpen}
        onOpenChange={setMediaOpen}
      />
      <MessageThread
        messages={messages.data ?? []}
        participants={participants}
        currentUserId={currentUserId}
        isLoading={messages.isPending}
        error={messages.error as Error | null}
        onRetry={() => void messages.refetch()}
        onDelete={handleDelete}
        deletingId={deletingId}
        typingLabel={typing}
        isGroup={isGroup}
        hasOlder={messages.hasNextPage}
        loadingOlder={messages.isFetchingNextPage}
        olderError={messages.isFetchNextPageError}
        onLoadOlder={loadOlder}
        focusMessageId={focusMessageId}
        onFocused={onFocused}
        className={cn("flex-1", threadClassName)}
      />
      {closed ? (
        <div className="border-t bg-muted/40 px-4 py-3 text-center text-sm text-muted-foreground">
          This conversation is closed because its project has ended. You can
          still read everything above.
        </div>
      ) : (
        <MessageComposer
          onSend={handleSend}
          onUpload={handleUpload}
          onTyping={handleTyping}
          disabled={status === "offline"}
          placeholder={
            status === "offline" ? "Reconnecting" : "Write a message"
          }
        />
      )}
    </div>
  );
}

/**
 * Title for a conversation row.
 *
 * The list response carries its own participants, so naming a direct message
 * costs no extra request.
 */
export function titleForConversation(
  conversation: ChatConversation,
  currentUserId: string | null | undefined,
) {
  return conversationTitle(
    conversation,
    conversation.participants ?? [],
    currentUserId,
  );
}
